








































import Vue from 'vue';
import Component from 'vue-class-component';
import {Prop, Watch} from 'vue-property-decorator';
import {debounce} from 'lodash';
import {SettingConstants} from '@/services/settings.service';
import BindAttrs from '@/utils/binds-attrs';
import AmetrasSimpleTextOutputTemplate from '@/fields/AmetrasSimpleTextOutputTemplate.vue';
import {FieldStyleContext} from '@/fields/field-style-context';
import {BROWSER_DETECTION_SERVICE_INSTANCE} from '@/services/browser-detection.service';

type rulesFunction = (currentValue: string) => string | boolean;
/**
 * General Component for Fields of type TEXT
 *
 * https://confluence.ametras.com/x/L4RQAw
 */
@Component({
  components: {AmetrasSimpleTextOutputTemplate},
  mixins: [BindAttrs]
})
export default class AmetrasTextFieldTemplate extends Vue {

  @Prop() textAlign!: 'LEFT' | 'RIGHT' | 'CENTER' | 'JUSTIFIED';
  @Prop() marginDefinition!: string;
  @Prop() paddingDefinition!: string;
  @Prop() value!: string;
  @Prop() debounce!: boolean;
  @Prop() upperCase!: boolean;
  @Prop() readonly!: boolean;
  @Prop({
    default: () => {
      return 'text';
    }
  }) inputType!: DisguiseDto.EnumInputTypeDto;
  @Prop() customRules!: [rulesFunction | string | boolean];
  /**
   * Indicates that we should render a simple mode of this component (increases the rendering performance)
   */
  @Prop() simple!: boolean;
  /**
   * Indicates that all errors should be shown
   */
  @Prop() forceShowErrors!: boolean;
  /**
   * This is a flag indicating that the placeholder should not be cleared if readonly mode is active.
   */
  @Prop({
    default: true
  }) clearPlaceholderIfEmpty!: boolean;
  @Prop({
    default: false
  }) required!: boolean;
  /**
   * any external error present
   */
  @Prop() error!: boolean;
  @Prop() typeSpecificSettings!: DisguiseDto.TextFieldSpecificSettings;
  @Prop() styleContext!: FieldStyleContext;

  modifiedlisteners: Record<string, any> = {};
  internalValue: string = '';

  rules = [(currentValue: string) => {
    if (this.required && (!currentValue || currentValue.length === 0) && (this.forceShowErrors || !this.readonly)) {
      return 'Bitte geben Sie einen Wert an!';
    }
    if ((this.required || (currentValue && currentValue.length >= 0))
        && this.typeSpecificSettings
        && this.typeSpecificSettings.validationRegExp
        && !new RegExp(this.typeSpecificSettings.validationRegExp, 'g').test(currentValue)) {
      return 'Bitte korrigieren Sie die Eingabe!';
    }
    const notHasAnyExternalError = typeof this.error === 'boolean' ? !this.error : true;
    return notHasAnyExternalError; // if any error from outside or any local error, return false
  }];

  $refs!: {
    inputfield: Vue
  };

  private originalInputListeners: any[] = [];
  private originalChangeListeners: any[] = [];
  // holds the last external value to prevent loops (emitting of input/change event -> modelChanges)
  private lastKnownExternalValue: string | null = null;

  private debounceInputFunction = debounce((newValue: string) => {
    this.publishInputEvent(newValue);
  }, SettingConstants.DEBOUNCE_INPUT_MS);

  private debounceChangeFunction = debounce((newValue: string) => {
    this.publishChangeEvent(newValue);
  }, SettingConstants.DEBOUNCE_INPUT_MS);

  @Watch('value') modelChanges(newValue: string | null | undefined) {
      if (this.upperCase && newValue !== undefined && newValue !== null) {
        this.internalValue = this.toUpperCase(newValue);
      } else {
        this.internalValue = newValue || '';
      }
      this.lastKnownExternalValue = this.internalValue;
      //this.refreshValidation();
      if (!this.simple) {
        try {
          const vuetifyTextField = this.$refs.inputfield as any;
          // sometimes the input field does not update the value properly. we override internal stuff to get a proper update
          if (vuetifyTextField.internalValue !== this.internalValue) {
            this.applyValueKeepingCursor(vuetifyTextField, this.internalValue);
          }
        } catch (error) {
          // ignore
        }
      }
      this.refreshValidation();
  }

  @Watch('required') requiredChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      if (newValue === false) {
        this.refreshValidation();
      }
    }
  }

  @Watch('error') errorChanges(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      this.refreshValidation();
    }
  }

  firefoxInterceptorForMissingChangeCall() {
    try {
      if (BROWSER_DETECTION_SERVICE_INSTANCE.isFirefox()) {
        this.$nextTick(() => {
          const vuetifyTextField = this.$refs.inputfield as any;
          // vuetify caches the value sent via input event
          // if the field is in focus state, the updateValue method triggeres the change event
          // for firefox and for field updates via autocompletion, the focus is not present and the updateValue method is never called.
          if (!vuetifyTextField.isFocused) {
            vuetifyTextField.updateValue();
          }
        });
      }
    } catch (error) {
      // ignore
    }
  }

  created() {
    this.lastKnownExternalValue = this.value;
    this.internalValue = this.value;
    if (this.$listeners.input) { // listeners$ comes from the BindAttrs mixin
      if (Array.isArray(this.$listeners.input)) {
        this.originalInputListeners = this.originalInputListeners.concat(this.$listeners.input);
      } else {
        this.originalInputListeners.push(this.$listeners.input);
      }
    }
    if (this.$listeners.change) { // listeners$ comes from the BindAttrs mixin
      if (Array.isArray(this.$listeners.change)) {
        this.originalChangeListeners = this.originalChangeListeners.concat(this.$listeners.change);
      } else {
        this.originalChangeListeners.push(this.$listeners.change);
      }
    }

    this.modifiedlisteners = {
      ...this.$listeners,
      input: (newValue: string) => {
        this.convertInputEvent(newValue);
      },
      change: (newValue: string) => {
        this.convertChangeEvent(newValue);
      }
    };
  }

  /**
   * Converts the original Value and finally publishes the value
   */
  private convertChangeEvent(newValue: string) {
    let convertedValue = newValue;
    if (this.upperCase) {
      convertedValue = this.toUpperCase(newValue);
    }
    if (this.debounce && this.debounceChangeFunction) {
      this.debounceChangeFunction(convertedValue);
    } else {
      this.publishChangeEvent(convertedValue);
    }
  }

  /**
   * Converts the original Value and finally publishes the value
   */
  private convertInputEvent(newValue: string) {
    let convertedValue = newValue;
    if (this.upperCase) {
      convertedValue = this.toUpperCase(newValue);
    }
    if (this.debounce && this.debounceInputFunction) {
      this.debounceInputFunction(convertedValue);
    } else {
      this.publishInputEvent(convertedValue);
    }
  }

  /**
   * Publishes the value to the original change listeners
   */
  private publishChangeEvent(value: string) {
    this.lastKnownExternalValue = value;
    for (const fnc of this.originalChangeListeners) {
      fnc(value);
    }
    this.updateVuetifyText();
  }

  /**
   * Publishes the value to the original input listeners
   */
  private publishInputEvent(value: string) {
    this.lastKnownExternalValue = value;
    for (const fnc of this.originalInputListeners) {
      fnc(value);
    }
    this.updateVuetifyText();
  }

  private updateVuetifyText() {
      try {
        const vuetifyTextField = this.$refs.inputfield as any;
        if (vuetifyTextField.internalValue !== this.lastKnownExternalValue) {
          this.applyValueKeepingCursor(vuetifyTextField, this.lastKnownExternalValue);
        }
      } catch (error) {
        //simply ignore
      }
  }

  /**
   * Writes the value into the vuetify text field without losing the caret position.
   * Patching the value property of the input element resets the caret to the end of the field,
   * which would make it impossible to correct an existing value in the middle (TRP-3313).
   */
  private applyValueKeepingCursor(vuetifyTextField: any, newValue: string | null) {
    const inputElement = vuetifyTextField.$refs.input as HTMLInputElement | undefined;
    if (inputElement === undefined || inputElement !== document.activeElement
        || inputElement.value.toLowerCase() !== (newValue || '').toLowerCase()) {
      // the caret is only worth keeping while the user edits the field and while the new value
      // differs in its casing only, which is exactly the conversion done for upperCase fields.
      // every other value change keeps the previous behaviour of jumping to the end of the field.
      vuetifyTextField.internalValue = newValue;
      return;
    }
    const selectionStart = inputElement.selectionStart;
    const selectionEnd = inputElement.selectionEnd;
    const previousLength = inputElement.value.length;

    vuetifyTextField.internalValue = newValue;

    if (selectionStart === null) {
      // input types like number do not provide a selection
      return;
    }
    // casing rules may still change the length of the value, so the caret is moved along with it
    const offset = (newValue || '').length - previousLength;
    this.$nextTick(() => {
      try {
        if (inputElement !== document.activeElement) {
          return;
        }
        const maxPosition = inputElement.value.length;
        const clamp = (position: number) => Math.min(Math.max(position + offset, 0), maxPosition);
        inputElement.setSelectionRange(clamp(selectionStart), clamp(selectionEnd === null ? selectionStart : selectionEnd));
      } catch (error) {
        // input types like number do not support selection ranges
      }
    });
  }

  private refreshValidation() {
    if (!this.simple) {
      const inputFieldRef = this.$refs.inputfield as any;
      if (inputFieldRef !== undefined) {
        inputFieldRef.validate();
      } else {
        throw new Error('Cannot refresh validation, the input field is not available');
      }
    }
  }

  /**
   * default toUpperCase of typescript would cause 'ß' to be cast to 'SS',
   * which may break length constraints
   */
  private toUpperCase(str: string): string {
    return str.split('').map(c => c === 'ß' ? 'ß' : c.toUpperCase()).join('');
  }

}

